import { useQuery as useConvexQuery } from "convex/react";
import { FunctionArgs, FunctionReference, FunctionReturnType } from "convex/server";
import { useEffect, useState, useMemo, useRef } from "react";
import { db } from "@/lib/dexie-db";
import {
    clearMemoryCached,
    getMemoryCached,
    setMemoryCached,
} from "@/lib/query-memory-cache";
import { useCacheStatusReporter } from "@/contexts/cache-status-context";

export type CacheTable =
    | "satelliteData"
    | "wsaData"
    | "auroraReports"
    | "userData"
    | "cloudReports"
    | "dIndexModelArtifacts"
    | "spaceWeather"
    | "publicLocations";

/** Drop cached query results so the next read refetches from Convex. */
export async function invalidateCachedQuery(
    table: CacheTable,
    cacheKey: string,
): Promise<void> {
    clearMemoryCached(table, cacheKey);
    try {
        await db[table].where("queryKey").equals(cacheKey).delete();
    } catch (error) {
        console.error("Error invalidating Dexie cache:", error);
    }
}

/**
 * Custom hook that wraps Convex useQuery with Dexie + in-memory caching.
 *
 * - Returns cached data synchronously from memory when available (instant tab switches)
 * - Falls back to IndexedDB, then Convex on first load
 * - Always keeps the Convex subscription active for live updates
 * - Serves stale local data while revalidating in the background
 */
export function useCachedQuery<Query extends FunctionReference<"query">>(
    query: Query,
    args: FunctionArgs<Query> | "skip",
    options?: {
        table?: CacheTable;
        ttl?: number;
        skipCache?: boolean;
        cacheKey?: string;
    },
) {
    const table = options?.table || "satelliteData";
    const ttl = options?.ttl ?? 5 * 60 * 1000;
    const skipCache = options?.skipCache || false;

    const queryKey = useMemo(() => {
        if (options?.cacheKey) return options.cacheKey;
        try {
            return JSON.stringify({ query: "convex-query", args });
        } catch {
            return `query-${Date.now()}`;
        }
    }, [options?.cacheKey, args]);

    const memorySnapshot = useMemo(
        () => (skipCache ? null : getMemoryCached<FunctionReturnType<Query>>(table, queryKey)),
        [skipCache, table, queryKey],
    );

    const [cachedData, setCachedData] = useState<FunctionReturnType<Query> | undefined>(
        () => memorySnapshot?.data,
    );
    const [cacheExpiresAt, setCacheExpiresAt] = useState<number | null>(
        () => memorySnapshot?.expiresAt ?? null,
    );
    const [isHydratingCache, setIsHydratingCache] = useState(
        () => !skipCache && memorySnapshot === null,
    );

    // Reset local state when the cache key changes (e.g. different query args)
    useEffect(() => {
        const mem = skipCache ? null : getMemoryCached<FunctionReturnType<Query>>(table, queryKey);
        setCachedData(mem?.data);
        setCacheExpiresAt(mem?.expiresAt ?? null);
        setIsHydratingCache(!skipCache && !mem);
    }, [queryKey, table, skipCache]);

    // Hydrate from IndexedDB when memory is cold (e.g. after page reload)
    useEffect(() => {
        if (skipCache) {
            setIsHydratingCache(false);
            return;
        }

        if (memorySnapshot) {
            setIsHydratingCache(false);
            return;
        }

        let cancelled = false;
        const loadFromCache = async () => {
            try {
                const result = await db.getCachedWithExpiry<FunctionReturnType<Query>>(
                    db[table],
                    queryKey,
                );
                if (cancelled) return;
                if (result) {
                    setCachedData(result.data);
                    setCacheExpiresAt(result.expiresAt);
                    setMemoryCached(table, queryKey, result.data, result.expiresAt);
                } else {
                    setCacheExpiresAt(null);
                }
            } catch (error) {
                console.error("Error loading from Dexie cache:", error);
                if (!cancelled) setCacheExpiresAt(null);
            } finally {
                if (!cancelled) setIsHydratingCache(false);
            }
        };
        void loadFromCache();
        return () => {
            cancelled = true;
        };
    }, [queryKey, table, skipCache, memorySnapshot]);

    const nowRef = useRef(0);
    nowRef.current = Date.now();
    const cacheIsFresh =
        cachedData !== undefined &&
        cacheExpiresAt !== null &&
        nowRef.current < cacheExpiresAt;
    const convexSkippedByArgs = args === "skip";
    const skipConvex = convexSkippedByArgs;

    const convexArgs = (skipConvex ? "skip" : args) as Parameters<typeof useConvexQuery<Query>>[1];
    const convexData = useConvexQuery(query, convexArgs);

    // Persist fresh Convex data locally and in memory
    useEffect(() => {
        if (convexData === undefined || skipCache) return;
        const updateCache = async () => {
            try {
                const expiresAt = Date.now() + ttl;
                const existing = await db.getCached(db[table], queryKey);
                if (JSON.stringify(existing) !== JSON.stringify(convexData)) {
                    await db.setCached(db[table], queryKey, convexData, ttl);
                }
                setCachedData(convexData);
                setCacheExpiresAt(expiresAt);
                setMemoryCached(table, queryKey, convexData, expiresAt);
            } catch (error) {
                console.error("Error saving to Dexie cache:", error);
            }
        };
        void updateCache();
    }, [convexData, queryKey, table, ttl, skipCache]);

    const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;

    const result = useMemo(() => {
        const hasData = convexData !== undefined || cachedData !== undefined;
        const servingStaleCacheWithoutLiveData =
            !cacheIsFresh &&
            convexData === undefined &&
            cachedData !== undefined;
        const isLoading = !hasData && (isHydratingCache || (!skipConvex && !convexSkippedByArgs));
        const isRefreshing =
            hasData &&
            servingStaleCacheWithoutLiveData &&
            !skipConvex &&
            !convexSkippedByArgs &&
            !isOffline;
        // Only flag cache fallback when offline, or when online but Convex is not actively revalidating.
        const fromCache =
            servingStaleCacheWithoutLiveData &&
            !convexSkippedByArgs &&
            (isOffline || !isRefreshing);

        if (convexData !== undefined) {
            return {
                data: convexData,
                isLoading: false,
                isRefreshing: false,
                fromCache: false,
                isOffline,
            };
        }
        if (cachedData !== undefined) {
            return {
                data: cachedData,
                isLoading: false,
                isRefreshing,
                fromCache,
                isOffline,
            };
        }
        return {
            data: undefined as FunctionReturnType<Query> | undefined,
            isLoading,
            isRefreshing: false,
            fromCache: false,
            isOffline,
        };
    }, [
        convexData,
        cachedData,
        isHydratingCache,
        skipConvex,
        cacheIsFresh,
        convexSkippedByArgs,
        isOffline,
    ]);

    const reportCacheStatus = useCacheStatusReporter();
    useEffect(() => {
        reportCacheStatus(queryKey, result.fromCache, result.isOffline);
        return () => {
            reportCacheStatus(queryKey, false, false);
        };
    }, [queryKey, result.fromCache, result.isOffline, reportCacheStatus]);

    return result;
}

